import React, { useState, useEffect } from 'react';
import { userSettingsAPI } from '../services/api';
import './Achievements.css';

const ICON_MAP = {
  trophy: 'fas fa-trophy',
  fire: 'fas fa-fire',
  star: 'fas fa-star',
  medal: 'fas fa-medal',
  bolt: 'fas fa-bolt',
  crown: 'fas fa-crown',
  gem: 'fas fa-gem',
  rocket: 'fas fa-rocket',
  bullseye: 'fas fa-bullseye',
  award: 'fas fa-award',
  flag: 'fas fa-flag-checkered',
  handshake: 'fas fa-handshake',
  users: 'fas fa-users',
  coins: 'fas fa-coins',
  chart: 'fas fa-chart-line',
};

function getIconClass(icon) {
  if (!icon) return 'fas fa-trophy';
  if (ICON_MAP[icon]) return ICON_MAP[icon];
  // If icon starts with "fas " or "fa-", use it directly
  if (icon.startsWith('fa')) return icon;
  return 'fas fa-trophy';
}

function isImageUrl(icon) {
  if (!icon) return false;
  return icon.startsWith('http') || icon.startsWith('/') || icon.endsWith('.png') || icon.endsWith('.jpg') || icon.endsWith('.svg');
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function Achievements() {
  const [earned, setEarned] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    userSettingsAPI.getAchievements()
      .then((data) => {
        setEarned(data.earned || []);
        setAvailable(data.available || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'Failed to load achievements');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="achievements-loading">
        <div className="spinner"></div>
        <div>Loading achievements...</div>
      </div>
    );
  }

  if (error) {
    return <div className="achievements-error">{error}</div>;
  }

  return (
    <div className="achievements-container">
      <h2>Achievements</h2>
      <p>Track your progress and see what you've unlocked.</p>

      {/* Earned */}
      <div className="achievements-section">
        <div className="achievements-section-title">
          <i className="fas fa-trophy"></i>
          Earned
          <span className="section-count">{earned.length}</span>
        </div>
        {earned.length === 0 ? (
          <div className="achievements-empty">
            <div className="empty-icon"><i className="fas fa-trophy"></i></div>
            <div>No achievements earned yet. Keep punting!</div>
          </div>
        ) : (
          <div className="achievements-grid">
            {earned.map((a) => (
              <div key={a.id} className="achievement-card earned">
                <div className="achievement-icon">
                  {isImageUrl(a.icon) ? (
                    <img src={a.icon} alt="" />
                  ) : (
                    <i className={getIconClass(a.icon)}></i>
                  )}
                </div>
                <div className="achievement-info">
                  <div className="achievement-name">{a.name}</div>
                  <div className="achievement-description">{a.description}</div>
                  {a.earnedAt && (
                    <div className="achievement-date">
                      <i className="fas fa-calendar-check"></i>
                      {formatDate(a.earnedAt)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Available */}
      {available.length > 0 && (
        <div className="achievements-section">
          <div className="achievements-section-title available">
            <i className="fas fa-lock"></i>
            Available
            <span className="section-count">{available.length}</span>
          </div>
          <div className="achievements-grid">
            {available.map((a) => (
              <div key={a.id} className="achievement-card available">
                <div className="achievement-icon">
                  {isImageUrl(a.icon) ? (
                    <img src={a.icon} alt="" />
                  ) : (
                    <i className={getIconClass(a.icon)}></i>
                  )}
                  <span className="lock-overlay">
                    <i className="fas fa-lock"></i>
                  </span>
                </div>
                <div className="achievement-info">
                  <div className="achievement-name">{a.name}</div>
                  <div className="achievement-description">{a.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
